/**
 * CADINU Lock tools: constants shared by the server, the pages and the tests. Nothing here reads the network.
 * The two lockers are the ones in config/constants/contracts.ts (cadinuLock = V2, cadinuLockV3 = V3); their verified sources are in
 * CADINU-Audit/source/contracts/verified (CadinuLockV2, LiquidityLockV3) and match exactly on Sourcify (checked 2026-09-30).
 */
export type LockType = 'v2' | 'v3'

export const CHAIN_ID = 56
export const LOCK_V3 = '0x9626aeA51924e1fE519164B47f971f0810e60Bc0' as const
export const LOCK_V2 = '0xc9C174f50A5c4A77Bc4d4D94890892DC29A94eEc' as const
export const CBON = '0x6e64fCF15Be3eB71C3d42AcF44D85bB119b2D98b' as const
export const CADINU = '0x76e112203eF59D445452ef7556386dD2DF3Ed914' as const
export const WBNB = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c' as const
export const ZERO = '0x0000000000000000000000000000000000000000' as const
export const MULTICALL3 = '0xcA11bde05977b3631167028862bE2a173976CA11' as const

/** The position managers the V3 locker accepts today (read from the locker on 2026-09-30) and what the site calls them. */
export const MANAGER_LABELS: Record<string, string> = {
  '0x0c26558a7bf8be790774fc84de8e5229a4db5ba1': 'CADINUdex V3',
  '0x46a15b0b27311cedf172ab29e4f4766fbe7f4364': 'PancakeSwap V3',
  '0x7b8a01b39d58278b5de7e48c8449c9f4f5170613': 'Uniswap V3',
}
export const managerLabel = (address: string, onChainName?: string | null) => MANAGER_LABELS[address.toLowerCase()] ?? (onChainName ? `${onChainName} V3` : 'V3 positions')
/** CADINUdex's own V2 factory: its pairs can be listed for a wallet; any other pair is pasted by address. */
export const CADINUDEX_V2_FACTORY = '0x5DaF6fEd0E85218B275DB7D86001621043b561DA' as const

export const CONTRACTS: Record<LockType, { address: string; name: string; label: string; holds: string }> = {
  v3: { address: LOCK_V3, name: 'LiquidityLockV3', label: 'CADINU Lock V3', holds: 'V3 liquidity positions (NFTs)' },
  v2: { address: LOCK_V2, name: 'CadinuLockV2', label: 'CADINU Lock V2', holds: 'V2 LP tokens and ordinary tokens' },
}
export const contractOf = (type: LockType) => CONTRACTS[type]
export const isLockType = (x: unknown): x is LockType => x === 'v2' || x === 'v3'

/** Explorer and badge links. BscScan's code tab shows the verified source; Sourcify holds the exact-match verification. */
export const BSCSCAN = 'https://bscscan.com'
export const addressUrl = (a: string) => `${BSCSCAN}/address/${a}`
export const codeUrl = (a: string) => `${BSCSCAN}/address/${a}#code`
export const txUrl = (h: string) => `${BSCSCAN}/tx/${h}`
export const sourcifyUrl = (a: string) => `https://repo.sourcify.dev/contracts/full_match/${CHAIN_ID}/${a}/`

/** The page of a lock, relative to the site root. */
export const lockPath = (type: LockType, id: number | string) => `/lock/${type}/${id}`
export const tokenLocksPath = (address: string) => `/lock/token/${address}`
export const LOCK_HOME = '/cadinu-lock'
export const CREATE_PATH = '/cadinu-lock/create'

/** A lock whose first release is within this many days is "unlocking soon". */
export const SOON_DAYS = 30
export const SOON_SECONDS = SOON_DAYS * 86400
/** A new lock may not end sooner than this after the block it is mined in (a wallet clock that runs fast must not fail the transaction). */
export const MIN_LOCK_SECONDS = 3600

/** Hosts whose absolute URLs the pages may print (Open Graph, certificates, QR codes); anything else falls back to production. */
export const PUBLIC_ORIGINS = ['https://apps.cadinu.io', 'https://stage-apps.cadinu.io']
export const DEFAULT_ORIGIN = 'https://apps.cadinu.io'

/** The list endpoints cache for this long; a page shows the age of its data. */
export const INDEX_TTL_MS = 60_000
export const PAGE_SIZE = 24
